import { FieldValidation } from '@/lib/utils/submitValidation';
import { ActivityTypeDto } from '@/api/ApiClient';
import { isHtmlContentEmpty } from '@/lib/utils/textUtils';

/** Save requirements for communication (format, title). Created-by is set by the backend on create and is not editable. */
export function createCommunicationSaveValidation(communication: {
  format?: string;
  title?: string;
}): FieldValidation[] {
  return [
    {
      key: 'format',
      label: 'Format',
      hasValue: !!communication.format?.trim(),
      value: communication.format,
    },
    {
      key: 'title',
      label: 'Title',
      hasValue: !!communication.title?.trim(),
      value: communication.title,
    },
  ];
}

/** Current save requirements for project activity details. */
export function createProjectActivitySaveValidation(activity: {
  desc?: string;
  userId?: string;
  date?: Date;
}): FieldValidation[] {
  return [
    {
      key: 'userId',
      label: 'User',
      hasValue: !!activity.userId?.trim(),
      value: activity.userId,
    },
    {
      key: 'date',
      label: 'Date',
      hasValue: !!activity.date,
      value: activity.date,
    },
    {
      key: 'desc',
      label: 'Description',
      hasValue: !!activity.desc?.trim(),
      value: activity.desc,
    },
  ];
}

/** Current save requirements for data package details. */
export function createDataPackageSaveValidation(dataPackage: {
  label?: string;
  description?: string;
  collaboratorIds?: string[];
}): FieldValidation[] {
  return [
    {
      key: 'label',
      label: 'Title',
      hasValue: !!dataPackage.label?.trim(),
      value: dataPackage.label,
    },
    {
      key: 'description',
      label: 'Description',
      hasValue: !!dataPackage.description && !isHtmlContentEmpty(dataPackage.description),
      value: dataPackage.description,
      validator: (val: string) => {
        if (!val || isHtmlContentEmpty(val)) {
          return 'Description is required';
        }
        return true;
      },
    },
    {
      key: 'collaboratorIds',
      label: 'Collaborators',
      hasValue: !!dataPackage.collaboratorIds && dataPackage.collaboratorIds.length > 0,
      value: dataPackage.collaboratorIds,
      validator: (val: string[]) => {
        if (!val || val.length === 0) {
          return 'At least one collaborator is required';
        }
        return true;
      },
    },
  ];
}

/** Current save requirements for simulation dataset details. */
export function createSimulationDatasetSaveValidation(data: {
  label?: string;
  description?: string;
}): FieldValidation[] {
  return [
    {
      key: 'label',
      label: 'Title',
      hasValue: !!data.label?.trim(),
      value: data.label,
    },
    {
      key: 'description',
      label: 'Description',
      hasValue: !!data.description?.trim(),
      value: data.description,
    },
  ];
}

/** Current save requirements for theory activity details. */
export function createTheoryActivitySaveValidation(data: {
  desc?: string;
  simulationTechnique?: string;
  userId?: string;
  date?: Date;
}): FieldValidation[] {
  return [
    {
      key: 'userId',
      label: 'User',
      hasValue: !!data.userId?.trim(),
      value: data.userId,
    },
    {
      key: 'date',
      label: 'Date',
      hasValue: !!data.date,
      value: data.date,
    },
    {
      key: 'desc',
      label: 'Activity Name',
      hasValue: !!data.desc?.trim(),
      value: data.desc,
    },
    {
      key: 'simulationTechnique',
      label: 'Method',
      hasValue: !!data.simulationTechnique?.trim(),
      value: data.simulationTechnique,
    },
  ];
}

/** Current save requirements for processing/recipe sample activities. */
export function createProcessingActivitySaveValidation(activity: {
  userId?: string;
  date?: Date;
  type?: string;
  instrument?: string;
}): FieldValidation[] {
  const fields: FieldValidation[] = [
    {
      key: 'userId',
      label: 'Researcher',
      hasValue: !!activity.userId?.trim(),
      value: activity.userId,
    },
    {
      key: 'date',
      label: 'Date',
      hasValue: !!activity.date,
      value: activity.date,
    },
    {
      key: 'type',
      label: 'Processing Type',
      hasValue: !!activity.type,
      value: activity.type,
    },
  ];

  if (activity.type === ActivityTypeDto.Synthesis) {
    fields.push({
      key: 'instrument',
      label: 'Instrument',
      hasValue: !!activity.instrument?.trim(),
      value: activity.instrument,
      validator: (val: string) => {
        if (!val || !val.trim()) return 'Synthesis requires an instrument';
        return true;
      },
    });
  }

  return fields;
}

/** Current save requirements for storage sample activities. */
export function createStorageActivitySaveValidation(activity: {
  userId?: string;
  date?: Date;
  sampleLocation?: string;
}): FieldValidation[] {
  return [
    {
      key: 'userId',
      label: 'Responsible Person',
      hasValue: !!activity.userId?.trim(),
      value: activity.userId,
    },
    {
      key: 'date',
      label: 'Date',
      hasValue: !!activity.date,
      value: activity.date,
    },
    {
      key: 'sampleLocation',
      label: 'Sample Location',
      hasValue: !!activity.sampleLocation?.trim(),
      value: activity.sampleLocation,
    },
  ];
}

/** Current save requirements for characterization sample activities. */
export function createCharacterizationActivitySaveValidation(activity: {
  userId?: string;
  date?: Date;
  facility?: string;
  visibility?: string;
  instrument?: string;
  characterizationTechnique?: string;
  desc?: string;
}): FieldValidation[] {
  const fields: FieldValidation[] = [
    {
      key: 'userId',
      label: 'Researcher',
      hasValue: !!activity.userId?.trim(),
      value: activity.userId,
    },
    {
      key: 'date',
      label: 'Date',
      hasValue: !!activity.date,
      value: activity.date,
    },
    {
      key: 'facility',
      label: 'Facility',
      hasValue: !!activity.facility?.trim(),
      value: activity.facility,
    },
    {
      key: 'visibility',
      label: 'Visibility',
      hasValue: !!activity.visibility,
      value: activity.visibility,
    },
  ];

  if (activity.facility === 'External') {
    fields.push(
      {
        key: 'characterizationTechnique',
        label: 'Technique',
        hasValue: !!activity.characterizationTechnique?.trim(),
        value: activity.characterizationTechnique,
      },
      {
        key: 'desc',
        label: 'Instrument',
        hasValue: !!activity.desc?.trim(),
        value: activity.desc,
      }
    );
  } else {
    fields.push({
      key: 'instrument',
      label: 'Instrument',
      hasValue: !!activity.instrument?.trim(),
      value: activity.instrument,
    });
  }

  return fields;
}

/** Baseline save requirements for sample activity variants sharing BasicActivityInfo. */
export function createSampleActivityBasicSaveValidation(activity: { userId?: string; date?: Date }): FieldValidation[] {
  return [
    {
      key: 'userId',
      label: 'User',
      hasValue: !!activity.userId?.trim(),
      value: activity.userId,
    },
    {
      key: 'date',
      label: 'Date',
      hasValue: !!activity.date,
      value: activity.date,
    },
  ];
}
